import React, { useState, useMemo } from "react";
import { ShoppingBag, Plus, Minus, Trash2, X, Heart, ShoppingCart } from "lucide-react";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetTrigger } from "../components/ui/sheet";
import { useToast } from "../hooks/use-toast";
import Reveal from "../components/Reveal";
import { products } from "../mock";

const categories = ["All", "Apparel", "Accessories", "Home", "Kids"];

const Shop = () => {
  const { toast } = useToast();
  const [filter, setFilter] = useState("All");
  const [cart, setCart] = useState([]); // {id, qty}
  const [open, setOpen] = useState(false);

  const filtered = useMemo(
    () => (filter === "All" ? products : products.filter((p) => p.category === filter)),
    [filter]
  );

  const add = (product) => {
    setCart((c) => {
      const found = c.find((i) => i.id === product.id);
      if (found) return c.map((i) => (i.id === product.id ? { ...i, qty: i.qty + 1 } : i));
      return [...c, { id: product.id, qty: 1 }];
    });
    toast({ title: "Added to cart", description: `${product.name} \u2014 $${product.price}` });
  };

  const changeQty = (id, delta) =>
    setCart((c) =>
      c.map((i) => (i.id === id ? { ...i, qty: Math.max(1, i.qty + delta) } : i))
    );

  const remove = (id) => setCart((c) => c.filter((i) => i.id !== id));

  const detailed = cart.map((i) => ({ ...i, ...products.find((p) => p.id === i.id) }));
  const count = cart.reduce((s, i) => s + i.qty, 0);
  const subtotal = detailed.reduce((s, i) => s + i.qty * i.price, 0);

  const checkout = () => {
    toast({
      title: "Thank you for supporting hope! \ud83d\udc9a",
      description: `Your order of $${subtotal} would be processed here. (Demo \u2014 no payment taken.)`,
    });
    setCart([]);
    setOpen(false);
  };

  return (
    <div>
      <section className="pt-32 lg:pt-40 pb-16 bg-secondary/40 relative overflow-hidden">
        <div className="absolute -top-20 right-10 h-72 w-72 rounded-full bg-accent/10 blur-3xl" />
        <div className="container-prc relative">
          <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-6 max-w-5xl">
            <div className="max-w-2xl">
              <Badge className="bg-white text-primary hover:bg-white rounded-full px-4 py-1.5 font-semibold mb-5">Shop for a Cause</Badge>
              <h1 className="font-display font-extrabold text-4xl lg:text-5xl text-foreground text-balance">Wear your hope, share your heart</h1>
              <p className="mt-5 text-lg text-muted-foreground">Every item you buy directly funds early-childhood programs for Dallas families. Good gifts that do good.</p>
            </div>
          </div>
        </div>
      </section>

      <section className="py-14">
        <div className="container-prc">
          <div className="flex flex-wrap items-center justify-between gap-4 mb-10">
            <div className="flex flex-wrap gap-3">
              {categories.map((c) => (
                <button
                  key={c}
                  onClick={() => setFilter(c)}
                  className={`px-5 py-2.5 rounded-full text-sm font-bold transition-colors ${
                    filter === c ? "bg-primary text-primary-foreground" : "bg-white text-foreground/70 hover:text-primary shadow-soft"
                  }`}
                >
                  {c}
                </button>
              ))}
            </div>

            <Sheet open={open} onOpenChange={setOpen}>
              <SheetTrigger asChild>
                <Button className="btn-cta relative bg-accent hover:bg-accent/90 text-accent-foreground font-bold rounded-full px-6">
                  <ShoppingCart className="h-5 w-5 mr-2" /> Cart
                  {count > 0 && (
                    <span className="absolute -top-2 -right-2 grid place-items-center h-6 w-6 rounded-full bg-primary text-primary-foreground text-xs font-bold">{count}</span>
                  )}
                </Button>
              </SheetTrigger>
              <SheetContent className="flex flex-col w-full sm:max-w-md">
                <SheetHeader>
                  <SheetTitle className="font-display flex items-center gap-2"><ShoppingBag className="h-5 w-5" /> Your Cart</SheetTitle>
                </SheetHeader>

                {detailed.length === 0 ? (
                  <div className="flex-1 grid place-items-center text-center text-muted-foreground">
                    <div>
                      <ShoppingCart className="h-12 w-12 mx-auto opacity-30" />
                      <p className="mt-4 font-semibold">Your cart is empty</p>
                      <p className="text-sm">Add something that gives back.</p>
                    </div>
                  </div>
                ) : (
                  <>
                    <div className="flex-1 overflow-y-auto -mx-1 px-1 py-4 space-y-4">
                      {detailed.map((i) => (
                        <div key={i.id} className="flex gap-3 bg-secondary/40 rounded-2xl p-3">
                          <img src={i.image} alt={i.name} className="h-16 w-16 rounded-xl object-cover" />
                          <div className="flex-1 min-w-0">
                            <p className="font-display font-bold text-sm text-foreground truncate">{i.name}</p>
                            <p className="text-sm text-muted-foreground">${i.price}</p>
                            <div className="flex items-center gap-2 mt-2">
                              <button onClick={() => changeQty(i.id, -1)} className="grid place-items-center h-7 w-7 rounded-full bg-white border border-border hover:border-primary"><Minus className="h-3.5 w-3.5" /></button>
                              <span className="w-6 text-center font-bold text-sm">{i.qty}</span>
                              <button onClick={() => changeQty(i.id, 1)} className="grid place-items-center h-7 w-7 rounded-full bg-white border border-border hover:border-primary"><Plus className="h-3.5 w-3.5" /></button>
                              <button onClick={() => remove(i.id)} className="ml-auto text-muted-foreground hover:text-destructive"><Trash2 className="h-4 w-4" /></button>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>

                    <div className="border-t border-border pt-4 space-y-4">
                      <div className="flex items-center justify-between font-display font-bold text-lg">
                        <span>Subtotal</span>
                        <span>${subtotal}</span>
                      </div>
                      <p className="text-xs text-muted-foreground text-center">Shipping & taxes calculated at checkout. Demo store — no real charge.</p>
                      <Button onClick={checkout} className="btn-cta w-full bg-primary hover:bg-primary/90 rounded-full font-bold h-12">
                        <Heart className="h-5 w-5 mr-2" /> {`Checkout \u2014 $${subtotal}`}
                      </Button>
                    </div>
                  </>
                )}
              </SheetContent>
            </Sheet>
          </div>

          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {filtered.map((p, i) => (
              <Reveal key={p.id} delay={(i % 4) * 70}>
                <div className="card-hover bg-white rounded-2xl overflow-hidden shadow-soft h-full flex flex-col">
                  <div className="img-zoom h-56 relative bg-secondary/40">
                    <img src={p.image} alt={p.name} className="w-full h-full object-cover" />
                    {p.badge && (
                      <Badge className="absolute top-3 left-3 bg-accent text-accent-foreground hover:bg-accent rounded-full font-semibold">{p.badge}</Badge>
                    )}
                  </div>
                  <div className="p-5 flex flex-col flex-1">
                    <span className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">{p.category}</span>
                    <h3 className="font-display font-bold text-foreground mt-1 leading-snug">{p.name}</h3>
                    <p className="mt-2 text-sm text-muted-foreground flex-1">{p.description}</p>
                    <div className="mt-4 flex items-center justify-between">
                      <span className="font-display font-extrabold text-xl text-primary">${p.price}</span>
                      <Button onClick={() => add(p)} className="btn-cta bg-accent hover:bg-accent/90 text-accent-foreground font-bold rounded-full">
                        <Plus className="h-4 w-4 mr-1.5" /> Add
                      </Button>
                    </div>
                  </div>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <section className="py-16 bg-secondary/40">
        <div className="container-prc">
          <Reveal className="bg-primary rounded-[2rem] p-10 lg:p-14 text-center text-primary-foreground">
            <h2 className="font-display font-extrabold text-3xl">100% of profits fund our mission</h2>
            <p className="mt-3 text-primary-foreground/85 max-w-2xl mx-auto">When you shop with us, you're not just getting great gear \u2014 you're helping a child and family in Dallas thrive.</p>
          </Reveal>
        </div>
      </section>
    </div>
  );
};

export default Shop;
